import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox, Check, Archive, RotateCcw, Pencil, Trash2, Plus } from "lucide-react";
import { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const TOKEN_KEY = "axiom-admin-token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [media, setMedia] = useState(null);
  const [filter, setFilter] = useState("all");
  const [mediaForm, setMediaForm] = useState({ date: "", title: "", body: "", article: "" });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (tok) => {
      setLoading(true);
      try {
        const headers = { "X-Admin-Token": tok };
        const [enqRes, subRes, mediaRes] = await Promise.all([
          axios.get(`${API}/enquiries`, { headers }),
          axios.get(`${API}/newsletter`, { headers }),
          axios.get(`${API}/media`),
        ]);
        setEnquiries(enqRes.data);
        setSubscribers(subRes.data);
        setMedia(mediaRes.data);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else {
          toast.error("Could not load enquiries");
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid credentials", { description: "Check the admin password and try again." });
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: { "X-Admin-Token": token } });
      setEnquiries((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update the enquiry");
    }
  };

  const resetMediaForm = () => {
    setEditingId(null);
    setMediaForm({ date: "", title: "", body: "", article: "" });
  };

  const saveMedia = async (e) => {
    e.preventDefault();
    try {
      const headers = { "X-Admin-Token": token };
      if (editingId) {
        await axios.put(`${API}/media/${editingId}`, mediaForm, { headers });
        setMedia((list) => list.map((m) => (m.id === editingId ? { ...m, ...mediaForm } : m)));
        toast.success("Card updated");
      } else {
        const res = await axios.post(`${API}/media`, mediaForm, { headers });
        setMedia((list) => [...(list || []), res.data]);
        toast.success("Card added");
      }
      resetMediaForm();
    } catch {
      toast.error("Could not save the card");
    }
  };

  const deleteMedia = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`, { headers: { "X-Admin-Token": token } });
      setMedia((list) => list.filter((m) => m.id !== id));
      toast.success("Card deleted");
    } catch {
      toast.error("Could not delete the card");
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setMediaForm({ date: m.date, title: m.title, body: m.body, article: m.article || "" });
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <Card className="w-full max-w-md border-ink/10 shadow-none">
          <CardHeader>
            <p className="text-xs uppercase tracking-[0.35em] text-cobalt font-medium">Axiom · Internal</p>
            <CardTitle className="font-display text-3xl tracking-tight">Enquiries Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                data-testid="admin-password-input"
                className="w-full bg-white border border-ink/15 rounded-lg px-4 py-3 text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-cobalt/50"
              />
              <button
                type="submit"
                data-testid="admin-login-button"
                className="w-full rounded-full bg-ink text-paper px-6 py-3 text-sm tracking-wide transition-colors duration-200 hover:bg-cobalt"
              >
                Sign in
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const newCount = enquiries?.filter((q) => q.status === "new").length ?? 0;
  const filtered = (enquiries || []).filter((q) => filter === "all" || q.status === filter);

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-cobalt font-medium">Axiom · Internal</p>
            <h1 className="mt-2 font-display text-4xl tracking-tight text-ink" data-testid="admin-heading">Enquiries</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(token)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink px-5 py-2.5 text-sm transition-colors duration-200 hover:border-cobalt hover:text-cobalt"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-5 py-2.5 text-sm transition-colors duration-200 hover:bg-cobalt"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-ink/10 shadow-none" data-testid="admin-stat-total">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.25em] text-ink/50 font-sans font-medium">Total enquiries</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-display text-5xl text-ink">{enquiries?.length ?? "—"}</p>
            </CardContent>
          </Card>
          <Card className="border-ink/10 shadow-none" data-testid="admin-stat-new">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.25em] text-ink/50 font-sans font-medium">Awaiting response</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-display text-5xl text-cobalt">{enquiries ? newCount : "—"}</p>
            </CardContent>
          </Card>
          <Card className="border-ink/10 shadow-none" data-testid="admin-stat-latest">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.25em] text-ink/50 font-sans font-medium">Latest enquiry</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-display text-2xl text-ink">
                {enquiries && enquiries.length > 0
                  ? new Date(enquiries[0].created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  : "—"}
              </p>
            </CardContent>
          </Card>
          <Card className="border-ink/10 shadow-none" data-testid="admin-stat-subscribers">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.25em] text-ink/50 font-sans font-medium">Newsletter subscribers</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-display text-5xl text-ink">{subscribers?.length ?? "—"}</p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-10 bg-white border border-ink/10 rounded-lg overflow-hidden" data-testid="admin-enquiries-table">
          <div className="px-6 py-4 border-b border-ink/10 flex flex-wrap items-center gap-2">
            {["all", "new", "responded", "archived"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`enquiry-filter-${f}`}
                className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                  filter === f ? "bg-ink text-paper" : "text-ink/50 hover:text-ink border border-ink/15"
                }`}
              >
                {f}{enquiries ? ` (${f === "all" ? enquiries.length : enquiries.filter((q) => q.status === f).length})` : ""}
              </button>
            ))}
          </div>
          {enquiries && filtered.length === 0 ? (
            <div className="py-24 flex flex-col items-center gap-4 text-ink/40">
              <Inbox size={32} />
              <p>{enquiries && enquiries.length === 0 ? "No enquiries yet. They will appear here as they arrive." : `No ${filter} enquiries.`}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Interest</TableHead>
                  <TableHead className="w-[30%]">Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(filtered || []).map((q) => (
                  <TableRow key={q.id} data-testid={`admin-enquiry-row-${q.id}`}>
                    <TableCell className="whitespace-nowrap text-ink/60">
                      {new Date(q.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </TableCell>
                    <TableCell className="font-medium text-ink">{q.name}</TableCell>
                    <TableCell className="text-ink/70">{q.email}</TableCell>
                    <TableCell className="text-ink/70">{q.company || "—"}</TableCell>
                    <TableCell className="text-ink/70">{q.service}</TableCell>
                    <TableCell className="text-ink/70 max-w-xs truncate" title={q.message}>{q.message}</TableCell>
                    <TableCell>
                      <Badge
                        variant={q.status === "new" ? "default" : "secondary"}
                        className={`${q.status === "new" ? "bg-cobalt hover:bg-cobalt" : ""} ${q.status === "archived" ? "opacity-60" : ""}`}
                      >
                        {q.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {q.status === "new" && (
                          <button
                            onClick={() => setStatus(q.id, "responded")}
                            data-testid={`enquiry-respond-${q.id}`}
                            className="inline-flex items-center gap-1 text-xs text-cobalt hover:underline"
                          >
                            <Check size={13} /> Responded
                          </button>
                        )}
                        {q.status !== "archived" ? (
                          <button
                            onClick={() => setStatus(q.id, "archived")}
                            data-testid={`enquiry-archive-${q.id}`}
                            className="inline-flex items-center gap-1 text-xs text-ink/50 hover:text-ink"
                          >
                            <Archive size={13} /> Archive
                          </button>
                        ) : (
                          <button
                            onClick={() => setStatus(q.id, "new")}
                            data-testid={`enquiry-restore-${q.id}`}
                            className="inline-flex items-center gap-1 text-xs text-ink/50 hover:text-ink"
                          >
                            <RotateCcw size={13} /> Restore
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="mt-10 bg-white border border-ink/10 rounded-lg overflow-hidden" data-testid="admin-media-manager">
          <div className="px-6 py-4 border-b border-ink/10">
            <p className="text-xs uppercase tracking-[0.25em] text-ink/50">Media section — news cards</p>
          </div>
          <form onSubmit={saveMedia} className="p-6 grid grid-cols-1 md:grid-cols-12 gap-4 border-b border-ink/10" data-testid="media-form">
            <input
              required
              value={mediaForm.date}
              onChange={(e) => setMediaForm((f) => ({ ...f, date: e.target.value }))}
              placeholder="Date label, e.g. Aug 2026"
              data-testid="media-date-input"
              className="md:col-span-2 bg-white border border-ink/15 rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-cobalt/50"
            />
            <input
              required
              value={mediaForm.title}
              onChange={(e) => setMediaForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Headline"
              data-testid="media-title-input"
              className="md:col-span-10 bg-white border border-ink/15 rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-cobalt/50"
            />
            <textarea
              required
              rows={2}
              value={mediaForm.body}
              onChange={(e) => setMediaForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Short summary shown on the card"
              data-testid="media-body-input"
              className="md:col-span-12 bg-white border border-ink/15 rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-cobalt/50 resize-none"
            />
            <textarea
              rows={6}
              value={mediaForm.article}
              onChange={(e) => setMediaForm((f) => ({ ...f, article: e.target.value }))}
              placeholder="Full article text (optional). Separate paragraphs with a blank line — shown on the article page."
              data-testid="media-article-input"
              className="md:col-span-12 bg-white border border-ink/15 rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-cobalt/50 resize-y"
            />
            <div className="md:col-span-12 flex items-center gap-4">
              <button
                type="submit"
                data-testid="media-save-button"
                className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-5 py-2.5 text-sm transition-colors duration-200 hover:bg-cobalt"
              >
                <Plus size={15} /> {editingId ? "Save changes" : "Add card"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetMediaForm}
                  data-testid="media-cancel-button"
                  className="text-sm text-ink/50 hover:text-ink"
                >
                  Cancel edit
                </button>
              )}
            </div>
          </form>
          <div className="divide-y divide-ink/10">
            {(media || []).map((m) => (
              <div key={m.id} className="px-6 py-4 flex items-start justify-between gap-6" data-testid={`media-row-${m.id}`}>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-cobalt">{m.date}</p>
                  <p className="mt-1 font-medium text-ink">{m.title}</p>
                  <p className="mt-1 text-sm text-ink/60">{m.body}</p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <button
                    onClick={() => startEdit(m)}
                    data-testid={`media-edit-${m.id}`}
                    className="inline-flex items-center gap-1 text-xs text-cobalt hover:underline"
                  >
                    <Pencil size={13} /> Edit
                  </button>
                  <button
                    onClick={() => deleteMedia(m.id)}
                    data-testid={`media-delete-${m.id}`}
                    className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 bg-white border border-ink/10 rounded-lg overflow-hidden" data-testid="admin-subscribers-table">
          <div className="px-6 py-4 border-b border-ink/10">
            <p className="text-xs uppercase tracking-[0.25em] text-ink/50">Newsletter subscribers</p>
          </div>
          {subscribers && subscribers.length === 0 ? (
            <div className="py-16 text-center text-ink/40">No subscribers yet. New signups from the website will appear here.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Subscribed on</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(subscribers || []).map((s) => (
                  <TableRow key={s.id} data-testid={`admin-subscriber-row-${s.id}`}>
                    <TableCell className="font-medium text-ink">{s.email}</TableCell>
                    <TableCell className="text-ink/60">
                      {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
